import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Download, Trash2, Share2, Copy } from "lucide-react";
import { http, defaultStatuses, money, notify, can } from "../api";
import { Status, PanelHead, Fact, Loading, Modal } from "./common";
import { LeadForm, ApplicationForm, FollowupForm, ActivityForm, DocumentForm } from "./forms";
import { CallingHistoryPanel } from "./SaAdmin";
import { showUndoToast } from "./Leads";
import SmartPolicySuggest from "./SmartPolicySuggest";

export default function LeadDetail({ leadId, user, onBack }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassignBoe, setShowReassignBoe] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    return http.get(`/leads/${leadId}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        setLoadError(err.response?.data?.detail || "This lead is no longer available.");
        notify.error(err, "Could not load lead");
      });
  };

  useEffect(() => { load(); }, [leadId]);

  if (loadError) {
    return (
      <>
        <button data-testid="lead-detail-back-button" className="back-button" onClick={onBack}>
          <ArrowLeft size={16} /> Back to leads
        </button>
        <section className="panel" data-testid="lead-detail-error">
          <PanelHead title="Lead unavailable" />
          <p className="empty compact-empty">{loadError}</p>
          <p className="muted small">
            The lead may have been deleted or reassigned outside your scope. Head back to your
            leads list to continue.
          </p>
        </section>
      </>
    );
  }
  if (!data) return <Loading>Loading customer record…</Loading>;
  const l = data.lead;

  const setStatus = async (status) => {
    if (!status || status === l.status) return;
    const reason = window.prompt(
      `Change status from "${l.status}" to "${status}".\n\nReason for change (required for audit trail):`,
    );
    if (reason === null) return;
    if (!reason.trim()) {
      notify.error("A reason is required for the audit trail.");
      return;
    }
    try {
      await http.patch(`/leads/${leadId}/status`, { status, reason: reason.trim() });
      notify.success(`Status set to ${status}`);
      load();
    } catch (err) {
      notify.error(err, "Could not update status");
    }
  };

  const download = async (doc) => {
    try {
      const r = await http.get(`/documents/${doc.document_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(err, "Could not download file");
    }
  };

  const removeDoc = async (doc) => {
    if (!window.confirm(`Delete “${doc.filename}”? This cannot be undone.`)) return;
    try {
      await http.delete(`/documents/${doc.document_id}`);
      notify.success("Document deleted");
      load();
    } catch (err) {
      notify.error(err, "Could not delete document");
    }
  };

  const deleteLead = async () => {
    setDeleting(true);
    try {
      const r = await http.delete(`/leads/${leadId}`);
      const c = r.data.cascade || {};
      const bits = [];
      if (c.applications) bits.push(`${c.applications} application${c.applications === 1 ? "" : "s"}`);
      if (c.documents) bits.push(`${c.documents} document${c.documents === 1 ? "" : "s"}`);
      if (c.followups) bits.push(`${c.followups} follow-up${c.followups === 1 ? "" : "s"}`);
      if (c.activities) bits.push(`${c.activities} activity note${c.activities === 1 ? "" : "s"}`);
      const detail = bits.length ? ` · cleared ${bits.join(", ")}` : "";
      onBack();
      showUndoToast({
        restoreId: r.data.restore_id,
        summary: `${data.lead.customer_name} deleted${detail}`,
        onRestored: onBack,
      });
    } catch (err) {
      notify.error(err, "Could not delete lead");
      setDeleting(false);
    }
  };

  const canDeleteDocs = can(user, "delete_documents");
  const canUploadDocs = can(user, "upload_documents");
  const canFollowup = can(user, "add_followups");
  // Strict RBAC: only admins can freely edit lead details. Back office roles
  // can edit but limited to Bank/product/loan-amount (server enforces).
  // Everyone else can only view.
  const isBackOffice = user.role === "back_office_manager" || user.role === "back_office_executive";
  const isSaReviewOwner = user.role === "telecaller"
    && l?.assigned_to === user.id
    && l?.review_status === "pending_review";
  const canEdit = user.role === "admin" || isBackOffice || isSaReviewOwner;
  const canChangeStatus = user.role === "admin" || isBackOffice;
  const canApplication = user.role !== "back_office_executive"; // executives view but don't create apps
  const canDeleteLead = user.role === "admin";
  const canManualReassignBoe = ["admin", "business_head", "back_office_manager"].includes(user.role);

  return (
    <>
      <button data-testid="lead-detail-back-button" className="back-button" onClick={onBack}>
        <ArrowLeft size={16} /> Back to leads
      </button>

      <div className="detail-heading">
        <div>
          <p className="eyebrow">CUSTOMER / {l.lead_id}</p>
          <h1>{l.customer_name}</h1>
          <p className="muted">
            {l.mobile} · {l.email || "No email"} · {l.city || "Location not set"}
          </p>
        </div>
        <div className="detail-actions">
          <Status value={l.status} />
          {l.review_status === "pending_review" && (
            <span
              data-testid="lead-review-pending-badge"
              style={{
                border: "1px solid #b45309", color: "#b45309",
                background: "#fef3c7", padding: "5px 8px", fontSize: 10,
                fontWeight: 700, letterSpacing: ".1em",
              }}
            >
              FOR REVIEW
            </span>
          )}
          {l.review_status === "pending_review"
            && (user.role === "admin" || l.assigned_to === user.id) && (
            <button
              data-testid="lead-send-to-login-button"
              className="primary-button"
              onClick={async () => {
                if (!window.confirm(
                  "Send this lead to Login? After this, it moves out of your For Review list and to Back Office."
                )) return;
                try {
                  await http.post(`/leads/${leadId}/send-to-login`);
                  notify.success("Lead sent to Login");
                  load();
                } catch (err) { notify.error(err, "Could not send to Login"); }
              }}
              title="Move this lead out of Pending Review into the Login stage"
            >
              Send to Login
            </button>
          )}
          {canUploadDocs && (
            <button
              data-testid="lead-share-button"
              className="outline-button"
              onClick={() => setModal("share")}
            >
              <Share2 size={14} /> Share with customer
            </button>
          )}
          {canEdit && (
            <button
              data-testid="lead-detail-edit-button"
              className="outline-button"
              onClick={() => setModal("edit")}
            >
              {isBackOffice ? "Update bank / amount" : (isSaReviewOwner ? "Edit lead (review)" : "Edit lead")}
            </button>
          )}
          {canDeleteLead && (
            <button
              data-testid="lead-detail-delete-button"
              className="outline-button danger-button"
              onClick={() => setModal("delete")}
              title="Delete lead and cascade all its records"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <main>
          <section className="panel customer-facts">
            <PanelHead title="Customer profile" />
            <div className="facts-grid">
              <Fact label="Mobile" value={l.mobile} />
              <Fact label="PAN" value={l.pan || "Not provided"} sensitive={!!l.pan} />
              <Fact label="Employment" value={l.employment_type} />
              <Fact label="Company" value={l.company || "Not provided"} />
              <Fact label="Designation" value={l.designation || "Not provided"} />
              <Fact label="Monthly income" value={money(l.monthly_income)} />
              <Fact label="Existing EMI" value={money(l.existing_emi)} />
              <Fact label="Loan requirement" value={money(l.loan_amount)} />
              <Fact label="Loan type" value={l.loan_type} />
              <Fact label="Purpose" value={l.loan_purpose || "Not specified"} />
              <Fact label="Preferred bank" value={l.bank_name || "Not decided"} />
              <Fact label="Preferred product" value={l.product_name || "Not decided"} />
            </div>
          </section>

          <SmartPolicySuggest lead={l} />

          <section className="panel">
            <div className="section-title">
              <h3>Applications</h3>
              {canApplication && (
                <button
                  data-testid="application-create-button"
                  className="outline-button"
                  onClick={() => setModal("application")}
                >
                  <Plus size={15} /> Add application
                </button>
              )}
            </div>
            {data.applications.map((a) => (
              <div
                className="application-card"
                data-testid={`lead-application-${a.application_id}`}
                key={a.application_id}
              >
                <div>
                  <strong className="mono">{a.application_id}</strong>
                  <span>
                    {a.bank_name || a.bank_id} · {a.product_name || a.product_id}
                    {a.application_number ? ` · ${a.application_number}` : ""}
                  </span>
                  {a.commission_amount ? (
                    <span className="cell-sub payout-hint">
                      Payout {money(a.commission_amount)}
                      {a.commission_percent ? ` (${a.commission_percent}%)` : ""}
                      {a.commission_status ? ` · ${a.commission_status}` : ""}
                    </span>
                  ) : null}
                </div>
                <div>
                  <strong>{money(a.requested_amount)}</strong>
                  <Status value={a.status} />
                </div>
              </div>
            ))}
            {!data.applications.length && <div className="empty">No applications connected yet.</div>}
          </section>

          <section className="panel">
            <div className="section-title">
              <h3>Activity timeline</h3>
              <button
                data-testid="activity-create-button"
                className="outline-button"
                onClick={() => setModal("activity")}
              >
                <Plus size={15} /> Add note
              </button>
            </div>
            {data.activities.map((a, i) => (
              <div className="timeline-item" data-testid={`activity-row-${i}`} key={a.created_at + i}>
                <span className="timeline-dot" />
                <div>
                  <strong>{a.action}</strong>
                  <small>{a.notes || "Recorded by workflow"} · {a.user}</small>
                  <time>{a.created_at?.slice(0, 16).replace("T", " · ")}</time>
                </div>
              </div>
            ))}
            {!data.activities.length && <div className="empty">No activity recorded yet.</div>}
          </section>

          {can(user, "view_calling_activity") && (
            <section className="panel" data-testid="lead-calling-history-section">
              <div className="section-title">
                <h3>Calling history</h3>
              </div>
              <CallingHistoryPanel leadId={leadId} />
            </section>
          )}
        </main>

        <aside>
          <section className="panel side-panel">
            <p className="eyebrow">OWNERSHIP</p>
            <div className="owner-block">
              <span className="scope-dot" />
              <div>
                <small>Sales team</small>
                <strong data-testid="ownership-sales-team">{l.sales_team_name || "—"}</strong>
              </div>
            </div>
            <div className="owner-block">
              <span className="mini-label">TELECALLER</span>
              <strong data-testid="ownership-telecaller">{l.assigned_name || "—"}</strong>
            </div>
            <div className="owner-block">
              <span className="mini-label">SALES MANAGER</span>
              <strong data-testid="ownership-sales-manager">{l.sales_manager_name || "Unassigned"}</strong>
            </div>
            <div className="owner-block">
              <span className="mini-label">CREATED BY</span>
              <strong>{l.created_by || "Team member"}</strong>
            </div>
            {l.back_office_team_name && (
              <div className="owner-block">
                <span className="mini-label">BACK OFFICE</span>
                <strong data-testid="ownership-back-office-team">{l.back_office_team_name}</strong>
                {l.back_office_manager_name && (
                  <small className="owner-sub" data-testid="ownership-back-office-manager">Manager: {l.back_office_manager_name}</small>
                )}
                {l.back_office_executive_name && (
                  <small className="owner-sub" data-testid="ownership-back-office-executive">Executive: {l.back_office_executive_name}</small>
                )}
                {canManualReassignBoe && (
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setShowReassignBoe(true)}
                    data-testid="lead-reassign-boe-button"
                  >
                    Reassign BOE
                  </button>
                )}
              </div>
            )}
            {canChangeStatus && (
              <label className="status-select">
                Lead status
                <select
                  data-testid="lead-status-select"
                  value={l.status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {defaultStatuses.map((x) => <option key={x}>{x}</option>)}
                </select>
              </label>
            )}
          </section>

          <section className="panel side-panel">
            <div className="section-title">
              <h3>Follow-ups</h3>
              {canFollowup && (
                <button
                  data-testid="follow-up-create-button"
                  className="icon-button red-icon"
                  onClick={() => setModal("followup")}
                  aria-label="Add follow-up"
                >
                  <Plus size={17} />
                </button>
              )}
            </div>
            {data.followups.map((f, i) => (
              <div className="side-item" data-testid={`follow-up-row-${i}`} key={f.created_at + i}>
                <strong>{f.due_at?.slice(0, 16).replace("T", " · ")}</strong>
                <small>{f.followup_type} · {f.notes}</small>
                {f.outcome && <small>Outcome: {f.outcome}</small>}
              </div>
            ))}
            {!data.followups.length && <div className="empty compact-empty">No follow-ups scheduled.</div>}
          </section>

          <section className="panel side-panel">
            <div className="section-title">
              <h3>Documents</h3>
              {canUploadDocs && (
                <button
                  data-testid="document-upload-button"
                  className="icon-button red-icon"
                  onClick={() => setModal("document")}
                  aria-label="Upload document"
                >
                  <Plus size={17} />
                </button>
              )}
            </div>
            {data.documents.map((d, i) => (
              <div className="side-item doc-item" data-testid={`document-row-${i}`} key={(d.document_id || d.stored_name) + i}>
                <div>
                  <strong>{d.document_type}</strong>
                  <small>{d.filename} · {d.uploaded_by}</small>
                </div>
                <div className="doc-actions">
                  {d.document_id && (
                    <button
                      data-testid={`document-download-${i}`}
                      className="icon-button"
                      onClick={() => download(d)}
                      aria-label="Download"
                      title="Download"
                    >
                      <Download size={15} />
                    </button>
                  )}
                  {canDeleteDocs && d.document_id && (
                    <button
                      data-testid={`document-delete-${i}`}
                      className="icon-button danger-icon"
                      onClick={() => removeDoc(d)}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!data.documents.length && <div className="empty compact-empty">No documents uploaded.</div>}
          </section>
        </aside>
      </div>

      {modal === "edit" && (
        <LeadForm lead={l} user={user} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === "application" && (
        <ApplicationForm
          leadId={leadId}
          defaultAmount={l.loan_amount}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {modal === "followup" && (
        <FollowupForm leadId={leadId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === "activity" && (
        <ActivityForm leadId={leadId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === "document" && (
        <DocumentForm leadId={leadId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {showReassignBoe && (
        <ReassignBoeModal
          lead={l}
          user={user}
          onClose={() => setShowReassignBoe(false)}
          onSaved={() => { setShowReassignBoe(false); load(); }}
        />
      )}
      {modal === "share" && (
        <ShareLinkModal
          leadId={leadId}
          customerName={l.customer_name}
          mobile={l.mobile}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "delete" && (
        <DeleteLeadModal
          lead={l}
          counts={{
            applications: data.applications.length,
            documents: data.documents.length,
            followups: data.followups.length,
            activities: data.activities.length,
          }}
          busy={deleting}
          onConfirm={deleteLead}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function DeleteLeadModal({ lead, counts, busy, onConfirm, onClose }) {
  const [typed, setTyped] = useState("");
  const needed = lead.lead_id;
  const total = counts.applications + counts.documents + counts.followups + counts.activities;

  return (
    <Modal title="Delete this lead?" onClose={onClose}>
      <div className="record-form">
        <p className="muted" style={{ marginTop: 0 }}>
          You’re about to permanently delete <strong>{lead.customer_name}</strong> (<span className="mono">{lead.lead_id}</span>).
          {total > 0
            ? " Everything attached to this lead will also be removed:"
            : " There is no linked activity to clear."}
        </p>

        {total > 0 && (
          <ul className="delete-cascade-list">
            {counts.applications > 0 && (
              <li>
                <strong>{counts.applications}</strong> application{counts.applications === 1 ? "" : "s"} (and their payouts)
              </li>
            )}
            {counts.documents > 0 && (
              <li>
                <strong>{counts.documents}</strong> document{counts.documents === 1 ? "" : "s"} — files removed from storage
              </li>
            )}
            {counts.followups > 0 && (
              <li>
                <strong>{counts.followups}</strong> follow-up{counts.followups === 1 ? "" : "s"}
              </li>
            )}
            {counts.activities > 0 && (
              <li>
                <strong>{counts.activities}</strong> activity note{counts.activities === 1 ? "" : "s"}
              </li>
            )}
          </ul>
        )}

        <div className="delete-audit-note">
          The audit trail of this lead is kept so administrators can see who deleted it and when.
        </div>

        <label>
          Type <span className="mono">{needed}</span> to confirm
          <input
            data-testid="delete-confirm-input"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={needed}
          />
        </label>

        <div className="form-actions" style={{ justifyContent: "space-between" }}>
          <button type="button" className="outline-button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-button danger-button"
            data-testid="delete-confirm-button"
            disabled={typed.trim() !== needed || busy}
            onClick={onConfirm}
          >
            {busy ? "Deleting…" : "Delete lead permanently"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ShareLinkModal({ leadId, customerName, mobile, onClose }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    http.get(`/leads/${leadId}/customer-link`)
      .then((r) => setLink(r.data.active ? r.data : null))
      .catch(() => setLink(null))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, [leadId]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await http.post(`/leads/${leadId}/customer-link`);
      setLink(r.data);
      notify.success("Private link ready");
    } catch (err) {
      notify.error(err, "Could not create link");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Withdraw the link so the customer can no longer upload?")) return;
    try {
      await http.delete(`/leads/${leadId}/customer-link`);
      setLink(null);
      notify.success("Link withdrawn");
    } catch (err) {
      notify.error(err, "Could not withdraw");
    }
  };

  const copy = async () => {
    if (!link?.upload_url) return;
    try {
      await navigator.clipboard.writeText(link.upload_url);
      notify.success("Link copied");
    } catch {
      notify.error("Copy failed — please long-press the link to copy");
    }
  };

  const openWhatsApp = async () => {
    let l = link;
    if (!l) {
      try {
        const r = await http.post(`/leads/${leadId}/customer-link`);
        l = r.data;
        setLink(l);
      } catch (err) {
        notify.error(err, "Could not create link");
        return;
      }
    }
    // If the link was fetched via GET, we may not have whatsapp_url stored.
    // Regenerate the WhatsApp URL client-side to be safe.
    const msg = `Hi ${customerName}, please upload your PAN, income proof and salary slips here: ${l.upload_url}\n\nThis private link is valid for 7 days.`;
    let digits = (mobile || "").replace(/\D/g, "");
    if (digits.length === 10) digits = "91" + digits;
    const wa = l.whatsapp_url || `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener");
  };

  return (
    <Modal title="Share private upload link" onClose={onClose}>
      <div className="record-form">
        <p className="muted" style={{ marginTop: 0 }}>
          A private page where {customerName} can upload PAN, income proof and salary slips —
          straight into this lead. Only they can use it, and only for 7 days.
        </p>

        {loading ? (
          <Loading>Preparing link…</Loading>
        ) : link ? (
          <>
            <label>
              Private upload URL
              <div className="share-url-row">
                <input
                  data-testid="share-link-url"
                  readOnly
                  value={link.upload_url}
                  onFocus={(e) => e.target.select()}
                />
                <button type="button" className="outline-button" onClick={copy} data-testid="share-link-copy">
                  <Copy size={14} /> Copy
                </button>
              </div>
              <small className="muted">
                Expires {link.expires_at ? new Date(link.expires_at).toLocaleDateString("en-IN") : "in 7 days"}
              </small>
            </label>
            <div className="form-actions" style={{ justifyContent: "space-between" }}>
              <button type="button" className="outline-button" onClick={revoke} data-testid="share-link-revoke">
                Withdraw link
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="outline-button" onClick={generate} data-testid="share-link-regenerate">
                  Regenerate
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={openWhatsApp}
                  data-testid="share-link-whatsapp"
                >
                  <Share2 size={14} /> Send on WhatsApp
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="upload-note">
              No active link yet. Create one — it opens WhatsApp with a ready-to-send message to {mobile}.
            </p>
            <div className="form-actions">
              <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="primary-button"
                onClick={openWhatsApp}
                data-testid="share-link-create"
              >
                <Share2 size={14} /> Create link &amp; open WhatsApp
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}


function ReassignBoeModal({ lead, user, onClose, onSaved }) {
  const [boes, setBoes] = useState([]);
  const [selected, setSelected] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/users").then((r) => {
      const eligible = r.data.filter(
        (u) => u.role === "back_office_executive" && u.active !== false
          && (user.role !== "back_office_manager" || u.back_office_team_id === user.back_office_team_id)
          && u.id !== lead.back_office_executive_id,
      );
      setBoes(eligible);
    }).catch(() => {});
  }, [lead.back_office_executive_id, user.role, user.back_office_team_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await http.patch(`/leads/${lead.lead_id}/reassign-boe`, {
        back_office_executive_id: selected,
        reason: reason.trim(),
      });
      notify.success("Lead reassigned");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not reassign");
      setBusy(false);
    }
  };

  return (
    <Modal title="Reassign Back Office Executive" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <p className="muted small">
          Currently assigned to <strong>{lead.back_office_executive_name || "—"}</strong>
          {" "}via <strong>{lead.bank_name || "no bank"}</strong>. Reassigning affects only this lead
          — the bank-wide routing rule is unchanged.
        </p>
        <label>
          New Back Office Executive
          <select
            data-testid="reassign-boe-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            required
          >
            <option value="">— pick an executive —</option>
            {boes.map((b) => (
              <option key={b.id} value={b.id}>{b.name} · {b.team_name || "Back office"}</option>
            ))}
          </select>
        </label>
        <label>
          Reason (optional)
          <textarea
            data-testid="reassign-boe-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Maya on leave for 2 weeks"
            rows={2}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="primary-button"
            disabled={busy || !selected}
            data-testid="reassign-boe-submit"
          >
            {busy ? "Reassigning…" : "Reassign"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
